import { useQuery } from '@tanstack/react-query';
import {
  AirtableStatus,
  TYPESENSE_COLLECTION,
} from 'gatsby-theme-hem-store/src/constants';

import { HitType } from '@/components/Search/Hit.types';

const COMMON_FILTERS = [
  'tags:!=[secret,algolia-ignore]',
  'type:=variant',
  `status:!=${AirtableStatus.PRE_ORDER}`,
];

const COMMON_EXCLUDES = 'embedding';

const useProductRecommendations = ({
  sku,
  currentCategory,
  categories,
  productName,
  hasDepartment,
}: {
  sku: string;
  currentCategory: string;
  categories: string[];
  productName: string;
  hasDepartment: boolean;
}) => {
  const { data, status } = useQuery({
    queryKey: ['product-recommendations', sku, categories],
    queryFn: async () => {
      const url = `https://${process.env.GATSBY_TYPESENSE_HOST}:${process.env.GATSBY_TYPESENSE_PORT}/multi_search`;
      const options = {
        method: 'POST',
        headers: {
          'X-Typesense-Api-Key': process.env.GATSBY_TYPESENSE_SEARCH_KEY,
        },
        body: JSON.stringify({
          searches: [
            // RELATED PRODUCTS
            {
              collection: TYPESENSE_COLLECTION,
              q: '*',
              per_page: 200,
              group_by: 'variantConfiguration',
              group_limit: 1,
              vector_query: `embedding:([], id: ${sku})`,
              filter_by: [`productName:!=${productName}`, ...COMMON_FILTERS]
                .filter(Boolean)
                .join(' && '),
              exclude_fields: COMMON_EXCLUDES,
            },
            // BESTSELLERS
            {
              collection: TYPESENSE_COLLECTION,
              q: '*',
              per_page: 8,
              group_by: 'variantConfiguration',
              group_limit: 1,
              sort_by: 'priority:desc',
              filter_by: [
                hasDepartment
                  ? `categories.lvl1:${currentCategory}`
                  : `categories.lvl0:${currentCategory}`,
                ...COMMON_FILTERS,
              ]
                .filter(Boolean)
                .join(' && '),
              exclude_fields: COMMON_EXCLUDES,
            },
            // CATEGORY
            ...categories.map((category) => ({
              collection: TYPESENSE_COLLECTION,
              q: '*',
              per_page: 8,
              group_by: 'productName',
              group_limit: 1,
              sort_by: 'priority:desc',
              filter_by: [
                hasDepartment
                  ? `categories.lvl2:${category}`
                  : `categories.lvl1:${category}`,
                ...COMMON_FILTERS,
              ]
                .filter(Boolean)
                .join(' && '),
              exclude_fields: COMMON_EXCLUDES,
            })),
          ],
        }),
      };

      const response = await fetch(url, options);
      const { results } = await response.json();

      const value = results.map(
        (result: { grouped_hits: { hits: { document: HitType }[] }[] }) => {
          return result.grouped_hits
            .map((groupedHit) => {
              return groupedHit.hits.map((hit) => hit.document);
            })
            .flat();
        },
      );

      return value;
    },
  });

  return { data, status };
};

export default useProductRecommendations;
